import { recordEvent } from '@department-of-veterans-affairs/platform-monitoring/exports';
import {
  createApiEvent,
  captureError,
  ERROR_SOURCES,
} from '../../utils/analytics';

const captureErrorToDatadog = (error, details) => {
  captureError(error, details);
};

/**
 * vets-api returns two shapes of "200 with an error body":
 *  - the CHIP wrapper `{ data: { error | errors }, status }`
 *  - top-level `{ error, message }` or `{ errors: [...] }` from LoROTA/sessions
 * Either one is a failed call and must reject like a non-2xx response would.
 *
 * @param {Object} json
 * @returns {Object|undefined} normalized error, or undefined when the body is clean
 */
const getErrorFromBody = json => {
  const { data } = json ?? {};
  const nested = data?.error || data?.errors;
  const topLevel = json?.error || json?.errors;
  if (!nested && !topLevel) {
    return undefined;
  }
  return {
    error: nested || topLevel,
    errors: json?.errors ?? data?.errors,
    data,
    status: json?.status ?? data?.status,
    code: data?.code,
  };
};

/**
 * @param {Promise} request
 * @param {string} [eventName]
 * @param {string} [token]
 * @param {function} [logEvent] used to log failed api calls
 * @param {boolean} [sendCode] append `data.code` to the analytics status
 */
const makeApiCall = async (
  request,
  eventName,
  token,
  logEvent = () => {},
  sendCode,
) => {
  // log call started
  recordEvent(createApiEvent(eventName, 'started'));
  // start the timer
  const startTime = new Date();
  // do the call
  const FAILED = 'failed';
  const SUCCESS = 'success';
  try {
    const json = await request;
    const endTime = new Date();
    const timeDiff = endTime.getTime() - startTime.getTime();

    const bodyError = getErrorFromBody(json);
    if (bodyError) {
      const code = sendCode ? ` - ${bodyError.code}` : '';
      recordEvent(
        createApiEvent(
          eventName,
          `${FAILED}${code}`,
          timeDiff,
          bodyError.error,
        ),
      );
      logEvent(
        {
          source: ERROR_SOURCES.API,
          err: bodyError.error,
        },
        { token, eventName },
      );
      const error = new Error(`${eventName}: api responded with an error body`);
      error.error = bodyError.error;
      error.errors = bodyError.errors;
      error.data = bodyError.data;
      error.status = bodyError.status;
      throw error;
    }

    recordEvent(createApiEvent(eventName, SUCCESS, timeDiff));
    return json;
  } catch (error) {
    if (!error?.error) {
      // a rejection from apiRequest (non-2xx, network); the error-body path
      // above has already recorded its own event
      recordEvent(createApiEvent(eventName, FAILED, null, error));
      logEvent(error, { token, eventName });
    }
    throw error;
  }
};

const makeApiCallWithSentry = async (
  request,
  eventName,
  token,
  sendCode = false,
) => {
  return makeApiCall(
    request,
    eventName,
    token,
    captureErrorToDatadog,
    sendCode,
  );
};
export { makeApiCall, makeApiCallWithSentry };
